import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GraduationCap, Eye, EyeOff, Loader2, User, Building2, Home, BookOpen, ArrowRight } from 'lucide-react';

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const isResident = typeParam === 'resident';
  const initialType = typeParam === 'org' ? 'organisation' : typeParam === 'resident' ? 'resident' : 'learner';

  const [activeTab, setActiveTab] = useState(initialType);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    company_name: '',
    company_address: '',
    company_phone: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: activeTab === 'organisation' ? 'org_admin' : 'learner',
        is_resident: isResident || activeTab === 'resident',
      };

      if (activeTab === 'organisation') {
        userData.company_name = formData.company_name;
        userData.company_address = formData.company_address;
        userData.company_phone = formData.company_phone;
      }

      await register(userData);

      if (isResident || activeTab === 'resident') {
        navigate('/courses?type=resident');
      } else if (activeTab === 'organisation') {
        navigate('/pending-approval');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Resident registration (simplified) ──
  if (isResident) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f0fdf4] via-[#f8fafc] to-[#f0fdf4] px-4 py-12" data-testid="register-page">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center justify-center gap-2.5 mb-6">
            <div className="w-11 h-11 bg-[#10B981] rounded-xl flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <span className="font-semibold text-xl text-[#1e293b]">Common Impact</span>
          </Link>

          <Card className="border-[#10B981]/20 shadow-lg bg-white" data-testid="resident-register-card">
            <CardHeader className="text-center pb-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#10B981]/10 rounded-full text-xs font-bold text-[#10B981] uppercase tracking-wider mx-auto mb-3">
                <Home className="h-3 w-3" /> Independence Builder
              </div>
              <CardTitle className="text-2xl font-bold text-[#1e293b]">Create your free account</CardTitle>
              <CardDescription className="text-[#1e293b]/60">
                Access free courses in digital skills, CV writing, and workplace readiness.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert className="bg-red-50 border-red-200 text-red-800">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="first_name" className="text-[#1e293b] text-sm">First name</Label>
                    <Input
                      id="first_name" name="first_name" placeholder="First name"
                      value={formData.first_name} onChange={handleChange} required
                      className="border-slate-200 focus:border-[#10B981] focus:ring-[#10B981]"
                      data-testid="register-first-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="last_name" className="text-[#1e293b] text-sm">Last name</Label>
                    <Input
                      id="last_name" name="last_name" placeholder="Last name"
                      value={formData.last_name} onChange={handleChange} required
                      className="border-slate-200 focus:border-[#10B981] focus:ring-[#10B981]"
                      data-testid="register-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-[#1e293b] text-sm">Email</Label>
                  <Input
                    id="email" name="email" type="email" placeholder="you@example.com"
                    value={formData.email} onChange={handleChange} required
                    className="border-slate-200 focus:border-[#10B981] focus:ring-[#10B981]"
                    data-testid="register-email"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-[#1e293b] text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      id="password" name="password" type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters" value={formData.password}
                      onChange={handleChange} required
                      className="border-slate-200 focus:border-[#10B981] focus:ring-[#10B981]"
                      data-testid="register-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-[#1e293b] text-sm">Confirm password</Label>
                  <Input
                    id="confirmPassword" name="confirmPassword" type="password"
                    placeholder="Confirm your password" value={formData.confirmPassword}
                    onChange={handleChange} required
                    className="border-slate-200 focus:border-[#10B981] focus:ring-[#10B981]"
                    data-testid="register-confirm-password"
                  />
                </div>

                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white h-11"
                  data-testid="register-submit"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</>
                  ) : (
                    <><BookOpen className="mr-2 h-4 w-4" /> Create Free Account</>
                  )}
                </Button>
              </form>

              {/* What you get */}
              <div className="mt-5 p-3 bg-[#10B981]/5 rounded-xl border border-[#10B981]/10">
                <p className="text-xs font-semibold text-[#10B981] mb-2">What you get — completely free:</p>
                <ul className="space-y-1.5">
                  {['Digital Skills for Work and Life', 'CV Writing and Interview Skills', 'Introduction to the Workplace'].map((c, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
                      <ArrowRight className="h-3 w-3 text-[#10B981] shrink-0" /> {c}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 text-center text-sm">
                <span className="text-[#1e293b]/60">Already have an account? </span>
                <Link to="/login" className="text-[#10B981] hover:text-[#059669] font-medium" data-testid="login-link">
                  Sign in
                </Link>
              </div>

              <div className="mt-3 text-center">
                <Link to="/register" className="text-xs text-slate-400 hover:text-[#20acf6] underline">
                  Looking for staff training? Register here instead
                </Link>
              </div>
            </CardContent>
          </Card>

          <p className="mt-5 text-center text-xs text-[#1e293b]/40">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-[#1e293b]/60">Terms of Service</Link>{' '}and{' '}
            <Link to="/privacy" className="underline hover:text-[#1e293b]/60">Privacy Policy</Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Standard registration (Individual / Organisation) ──
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#F8FAFC] to-[#f8fafc] px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <div className="w-11 h-11 bg-[#20acf6] rounded-xl flex items-center justify-center">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold text-xl text-[#1e293b]">Common Impact</span>
        </Link>

        <Card className="border-[#f8fafc] shadow-lg bg-white">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-[#1e293b]">Create your account</CardTitle>
            <CardDescription className="text-[#1e293b]/60">
              Start your learning journey today
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="grid w-full grid-cols-3 bg-[#f8fafc]">
                <TabsTrigger value="learner" className="flex items-center gap-1.5 text-xs sm:text-sm data-[state=active]:bg-[#20acf6] data-[state=active]:text-white" data-testid="tab-learner">
                  <User className="w-4 h-4 hidden sm:block" />
                  Individual
                </TabsTrigger>
                <TabsTrigger value="organisation" className="flex items-center gap-1.5 text-xs sm:text-sm data-[state=active]:bg-[#02d1d4] data-[state=active]:text-[#1e293b]" data-testid="tab-organisation">
                  <Building2 className="w-4 h-4 hidden sm:block" />
                  Organisation
                </TabsTrigger>
                <TabsTrigger value="resident" className="flex items-center gap-1.5 text-xs sm:text-sm data-[state=active]:bg-[#10B981] data-[state=active]:text-white" data-testid="tab-resident">
                  <Home className="w-4 h-4 hidden sm:block" />
                  Resident
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {activeTab === 'resident' && (
              <div className="mb-4 p-3 bg-[#10B981]/5 rounded-xl border border-[#10B981]/10">
                <p className="text-sm text-[#10B981] font-medium flex items-center gap-1.5">
                  <Home className="h-4 w-4" /> Free courses for residents in supported living
                </p>
                <p className="text-xs text-slate-500 mt-1">No payment required. Sign up and start learning immediately.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert className="bg-[#F4A261]/10 border-[#F4A261]/30 text-[#1e293b]">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-[#1e293b]">First name</Label>
                  <Input
                    id="first_name" name="first_name" placeholder="John"
                    value={formData.first_name} onChange={handleChange} required
                    className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                    data-testid="register-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-[#1e293b]">Last name</Label>
                  <Input
                    id="last_name" name="last_name" placeholder="Doe"
                    value={formData.last_name} onChange={handleChange} required
                    className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                    data-testid="register-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1e293b]">Email</Label>
                <Input
                  id="email" name="email" type="email" placeholder="you@example.com"
                  value={formData.email} onChange={handleChange} required
                  className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                  data-testid="register-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#1e293b]">Phone (optional)</Label>
                <Input
                  id="phone" name="phone" type="tel" placeholder="+44 123 456 7890"
                  value={formData.phone} onChange={handleChange}
                  className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                  data-testid="register-phone"
                />
              </div>

              {activeTab === 'organisation' && (
                <>
                  <div className="pt-4 border-t border-[#f8fafc]">
                    <h3 className="font-medium text-[#1e293b] mb-4">Organisation Details</h3>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name" className="text-[#1e293b]">Organisation name</Label>
                    <Input
                      id="company_name" name="company_name" placeholder="Your Organisation"
                      value={formData.company_name} onChange={handleChange}
                      required={activeTab === 'organisation'}
                      className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                      data-testid="register-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_address" className="text-[#1e293b]">Address</Label>
                    <Input
                      id="company_address" name="company_address" placeholder="123 Care Street, London"
                      value={formData.company_address} onChange={handleChange}
                      className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                      data-testid="register-company-address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_phone" className="text-[#1e293b]">Organisation phone</Label>
                    <Input
                      id="company_phone" name="company_phone" type="tel" placeholder="+44 123 456 7890"
                      value={formData.company_phone} onChange={handleChange}
                      className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                      data-testid="register-company-phone"
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#1e293b]">Password</Label>
                <div className="relative">
                  <Input
                    id="password" name="password" type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters" value={formData.password}
                    onChange={handleChange} required
                    className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                    data-testid="register-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1e293b]/40 hover:text-[#1e293b]/60">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#1e293b]">Confirm password</Label>
                <Input
                  id="confirmPassword" name="confirmPassword" type="password"
                  placeholder="Confirm your password" value={formData.confirmPassword}
                  onChange={handleChange} required
                  className="border-[#f8fafc] focus:border-[#20acf6] focus:ring-[#20acf6]"
                  data-testid="register-confirm-password"
                />
              </div>

              {activeTab === 'organisation' && (
                <Alert className="bg-[#f8fafc] border-[#02d1d4]/30">
                  <AlertDescription className="text-[#1e293b]/80 text-sm">
                    Organisation accounts require approval. You'll receive an email once your account is verified.
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit" disabled={loading}
                className="w-full bg-[#20acf6] hover:bg-[#3bb8f7] text-white"
                data-testid="register-submit"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</>
                ) : (
                  'Create account'
                )}
              </Button>
            </form>

            <div className="mt-5 text-center text-sm">
              <span className="text-[#1e293b]/60">Already have an account? </span>
              <Link to="/login" className="text-[#20acf6] hover:text-[#3bb8f7] font-medium" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-[#1e293b]/40">
          By creating an account, you agree to our{' '}
          <Link to="/terms" className="underline hover:text-[#1e293b]/60">Terms of Service</Link>{' '}and{' '}
          <Link to="/privacy" className="underline hover:text-[#1e293b]/60">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
